import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { getMyShopPrivate, countShopRedemptions } from '../../services/shop-service';

interface Row {
  id: string;
  responded_at: string | null;
  geo_bucket: 'at_counter' | 'far' | null;
  user_profiles: { display_name: string | null } | null;
}

/**
 * A record of what happened, not a queue to work.
 *
 * Replaces the pending-redeem backlog. That queue assumed an owner watching a
 * screen during service; in practice it silted up while customers stood at the
 * counter waiting on an approval nobody was coming to give. Under the counter
 * flow the barista's tap redeems on the spot, so there is nothing left to
 * approve — only something to look back on.
 *
 * Labelled REWARDS REDEEMED rather than "coffees given". A customer can burn a
 * reward away from the counter (they gain nothing by it — the reward is spent
 * and a barista still has to hand over the cup), so redeemed is the claim this
 * data supports. Calling it coffees-served would quietly make it a wrong
 * number in someone's head.
 */
export default function GiveawayStats({ shopId }: { shopId: string }) {
  const [rows, setRows] = useState<Row[]>([]);
  const [avgCents, setAvgCents] = useState<number | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [res, priv] = await Promise.all([
        supabase
          .from('redeem_requests')
          // display_name ONLY — never email. A café can act on a customer
          // without ever holding the customer (merchant-terms position).
          .select('id, responded_at, geo_bucket, user_profiles(display_name)')
          .eq('shop_id', shopId)
          .order('responded_at', { ascending: false })
          .limit(20),
        getMyShopPrivate(),
      ]);
      if (cancelled) return;
      // A failed read renders the empty state rather than taking the whole
      // owner dashboard down with it.
      setRows(((res as { data?: unknown })?.data as Row[]) || []);
      setAvgCents(priv?.avg_coffee_price_cents ?? null);
      // The list above is capped at 20 for display. Counting its rows would
      // silently plateau at 20 and understate the cost of the giveaway — count
      // server-side instead.
      const total = await countShopRedemptions(shopId);
      if (!cancelled) setTotalCount(total);
    })();
    return () => { cancelled = true; };
  }, [shopId]);

  const count = totalCount ?? rows.length;
  const costCents = avgCents === null ? null : count * avgCents;

  return (
    <div className="bg-white rounded-[10px] p-[18px]">
      <h3 className="font-grotesk font-bold text-[17px] text-primary mb-3.5">
        Rewards redeemed
      </h3>

      {count === 0 ? (
        <p className="font-grotesk font-medium text-[13px] text-body-grey">
          No rewards redeemed yet — they'll appear here as customers claim them.
        </p>
      ) : (
        <>
          <p className="font-grotesk font-bold text-[40px] leading-none text-primary">{count}</p>
          {costCents !== null && (
            <p className="font-grotesk font-medium text-[13px] text-body-grey mt-2">
              ≈ ${(costCents / 100).toFixed(2)} at your average cup price
            </p>
          )}
          <ul className="mt-3.5 space-y-2">
            {rows.map((r) => (
              <li key={r.id} className="flex justify-between items-center gap-3">
                <span className="font-body text-[13px] text-primary truncate">
                  {r.user_profiles?.display_name || 'Member'}
                </span>
                {/* Signal only — see migration 052. Nobody is blocked by this. */}
                {r.geo_bucket === 'at_counter' && (
                  <span className="font-heading text-[8px] text-[#2f9440] shrink-0">AT THE COUNTER</span>
                )}
                {r.geo_bucket === 'far' && (
                  <span className="font-heading text-[8px] text-gold-ink shrink-0">AWAY</span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
